import moment from 'moment';
import type { Moment } from 'moment';
import type { SortOrder } from 'antd/lib/table/interface';

import { ClientService } from '../../../shared/api/ClientService';
import type { LocaleObj } from '../../../locale/LocalizedStrings';
import {
  COMMAND_ACTION_REASON_TEXT_KEYS,
  COMMAND_CANCELLABLE_STATUSES,
  COMMAND_MAX_ACTION_IDS,
  COMMAND_OLDEST_DUE_WARNING_SECONDS,
  COMMAND_RETRYABLE_STATUSES,
  COMMAND_SORT_KEYS,
  COMMAND_STATUS_PRESENTATION,
  DAY_UNIT,
  DEFAULT_COMMAND_SORT,
  DEFAULT_EVENT_SORT,
  DISPLAY_DATE_FORMAT,
  EMPTY_GUID,
  EMPTY_VALUE,
  EVENT_SORT_KEYS,
  EVENT_STATUS_PRESENTATION,
  GUID_PATTERN,
  ID_SEPARATORS,
  ID_WRAPPERS,
  LABEL_SEPARATOR,
  MAX_INVALID_TOKENS_SHOWN,
  MAX_REPLAY_IDS,
  MAX_TOKEN_LENGTH_SHOWN,
  REPLAY_ITEM_PRESENTATION,
  SECONDS_PER_HOUR,
  SECONDS_PER_MINUTE,
  SORT_DIRECTIONS,
  TOKEN_ELLIPSIS,
  WATERMARK_AGE_WARNING_SECONDS,
} from './constants';
import type {
  ActionBlock,
  ActionReason,
  CommandAction,
  CommandStatus,
  DateRange,
  EventStatus,
  IActionFailure,
  IActionSummary,
  ICommandFilters,
  ICommandListParams,
  ICommandSort,
  IEventFilters,
  IEventListParams,
  IEventSort,
  IReplayItemPresentation,
  IReplayParse,
  IReplaySummary,
  ISort,
  IStatusPresentation,
  ReplayItemStatus,
} from './types';

const hasOwn = (target: object, key: PropertyKey): boolean => Object.prototype.hasOwnProperty.call(target, key);
const isObject = (value: unknown): value is object => typeof value === 'object' && value !== null;

export const formatText = (template: string, ...values: Array<string | number>): string =>
  values.reduce<string>((text, value, index) => text.split(`{${index}}`).join(String(value)), template);

export const canRetry = (status?: CommandStatus): boolean =>
  status !== undefined && COMMAND_RETRYABLE_STATUSES.includes(status);

export const canCancel = (status?: CommandStatus): boolean =>
  status !== undefined && COMMAND_CANCELLABLE_STATUSES.includes(status);

export const isSelectable = (status?: CommandStatus): boolean => canRetry(status) || canCancel(status);

const ACTION_ELIGIBILITY: Record<CommandAction, (status?: CommandStatus) => boolean> = {
  retry: canRetry,
  cancel: canCancel,
};

export const getActionBlock = (
  action: CommandAction,
  statuses: ReadonlyArray<CommandStatus | undefined>
): ActionBlock | undefined => {
  if (statuses.length === 0) return 'empty';
  if (statuses.length > COMMAND_MAX_ACTION_IDS) return 'tooMany';
  return statuses.every((status) => ACTION_ELIGIBILITY[action](status)) ? undefined : 'ineligible';
};

export const getActionBlockText = (block: ActionBlock, action: CommandAction, t: LocaleObj): string => {
  if (block === 'empty') return t.INTEGRATION_SELECT_FIRST;
  if (block === 'tooMany') return formatText(t.INTEGRATION_TOO_MANY_SELECTED, COMMAND_MAX_ACTION_IDS);
  return action === 'retry' ? t.INTEGRATION_RETRY_NOT_ELIGIBLE : t.INTEGRATION_CANCEL_NOT_ELIGIBLE;
};

const isKeyOf = <TKey extends string>(keys: Record<TKey, string>, key: unknown): key is TKey =>
  typeof key === 'string' && hasOwn(keys, key);

// An unknown column or a cleared sort order falls back to the default sorting.
const toSort = <TKey extends string>(
  keys: Record<TKey, string>,
  fallback: ISort<TKey>,
  columnKey: unknown,
  order?: SortOrder
): ISort<TKey> => (isKeyOf(keys, columnKey) && order ? { columnKey, order } : fallback);

const sortingOf = <TKey extends string>(keys: Record<TKey, string>, { columnKey, order }: ISort<TKey>): string =>
  `${keys[columnKey]} ${SORT_DIRECTIONS[order]}`;

export const toSortState = (columnKey: unknown, order?: SortOrder): ICommandSort =>
  toSort(COMMAND_SORT_KEYS, DEFAULT_COMMAND_SORT, columnKey, order);

export const toEventSortState = (columnKey: unknown, order?: SortOrder): IEventSort =>
  toSort(EVENT_SORT_KEYS, DEFAULT_EVENT_SORT, columnKey, order);

export const buildSorting = (sort: ICommandSort): string => sortingOf(COMMAND_SORT_KEYS, sort);

export const buildEventSorting = (sort: IEventSort): string => sortingOf(EVENT_SORT_KEYS, sort);

export const toDateRange = (range: ReadonlyArray<Moment | null> | null | undefined): DateRange | null => {
  const [from, to] = range ?? [];
  return from && to ? [from, to] : null;
};

const getDayBounds = (range: DateRange | null): { from?: Moment; to?: Moment } => ({
  from: range?.[0].clone().startOf(DAY_UNIT),
  to: range?.[1].clone().endOf(DAY_UNIT),
});

const toSkipCount = (page: number, pageSize: number): number => (page - 1) * pageSize;

export const buildListParams = (
  filters: ICommandFilters,
  sort: ICommandSort,
  page: number,
  pageSize: number
): ICommandListParams => {
  const { from, to } = getDayBounds(filters.createdRange);
  return {
    status: filters.status,
    commandType: filters.commandType || undefined,
    fileName: filters.fileName || undefined,
    createdFrom: from,
    createdTo: to,
    heldOnly: filters.heldOnly || undefined,
    sorting: buildSorting(sort),
    skipCount: toSkipCount(page, pageSize),
    maxResultCount: pageSize,
  };
};

export const buildEventListParams = (
  filters: IEventFilters,
  sort: IEventSort,
  page: number,
  pageSize: number
): IEventListParams => {
  const { from, to } = getDayBounds(filters.occurredRange);
  return {
    status: filters.status,
    eventType: filters.eventType || undefined,
    occurredFrom: from,
    occurredTo: to,
    sorting: buildEventSorting(sort),
    skipCount: toSkipCount(page, pageSize),
    maxResultCount: pageSize,
  };
};

export const formatAge = (seconds?: number | null): string => {
  if (seconds === undefined || seconds === null || seconds < 0) return EMPTY_VALUE;

  const hours = Math.floor(seconds / SECONDS_PER_HOUR);
  const minutes = Math.floor((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const restSeconds = Math.floor(seconds % SECONDS_PER_MINUTE);

  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} h`);
  if (minutes > 0) parts.push(`${minutes} min`);
  if (hours === 0 && (restSeconds > 0 || parts.length === 0)) parts.push(`${restSeconds} s`);
  return parts.join(' ');
};

export const formatDateTime = (value?: Moment | null): string => {
  const date = value ? moment(value) : undefined;
  return date?.isValid() ? date.local().format(DISPLAY_DATE_FORMAT) : EMPTY_VALUE;
};

const isAbove = (value: number | null | undefined, limit: number): boolean =>
  value !== undefined && value !== null && value > limit;

export const isOldestDueAtRisk = (seconds?: number | null): boolean =>
  isAbove(seconds, COMMAND_OLDEST_DUE_WARNING_SECONDS);

export const isWatermarkLate = (seconds?: number | null): boolean => isAbove(seconds, WATERMARK_AGE_WARNING_SECONDS);

export const hasLeftoverCommands = (summary?: ClientService.IntegrationSummaryDto): boolean => {
  const commands = summary?.commands;
  const unfinished =
    (commands?.pendingCount ?? 0) +
    (commands?.retryingCount ?? 0) +
    (commands?.needsAttentionCount ?? 0) +
    (commands?.deadLetteredCount ?? 0);
  return summary?.insolvIntegrationEnabled === false && unfinished > 0;
};

export const getStatusPresentation = (status?: CommandStatus): IStatusPresentation | undefined =>
  status !== undefined && hasOwn(COMMAND_STATUS_PRESENTATION, status) ? COMMAND_STATUS_PRESENTATION[status] : undefined;

export const getEventStatusPresentation = (status?: EventStatus): IStatusPresentation | undefined =>
  status !== undefined && hasOwn(EVENT_STATUS_PRESENTATION, status) ? EVENT_STATUS_PRESENTATION[status] : undefined;

// A status this page does not know (a newer backend) shows its number.
const labelOf = (presentation: IStatusPresentation | undefined, status: number | undefined, t: LocaleObj): string => {
  if (presentation) return t[presentation.labelKey];
  return status === undefined ? EMPTY_VALUE : String(status);
};

export const getStatusLabel = (status: CommandStatus | undefined, t: LocaleObj): string =>
  labelOf(getStatusPresentation(status), status, t);

export const getEventStatusLabel = (status: EventStatus | undefined, t: LocaleObj): string =>
  labelOf(getEventStatusPresentation(status), status, t);

export const getIds = (items: ReadonlyArray<{ id?: string }>): string[] =>
  items.map(({ id }) => id).filter((id): id is string => !!id);

const isActionReason = (reason: string): reason is ActionReason => hasOwn(COMMAND_ACTION_REASON_TEXT_KEYS, reason);

export const getActionReasonText = (reason: string | undefined, t: LocaleObj): string => {
  if (!reason) return t.INTEGRATION_REASON_NOT_DONE;
  return isActionReason(reason)
    ? t[COMMAND_ACTION_REASON_TEXT_KEYS[reason]]
    : formatText(t.INTEGRATION_REASON_NOT_DONE_CODE, reason);
};

const describeCommand = (command: ClientService.IntegrationCommandDto): string | undefined =>
  [command.fileName, command.commandType].filter(Boolean).join(LABEL_SEPARATOR) || undefined;

export const summariseActionResult = (
  result: ClientService.IntegrationActionResultDto,
  commands: ReadonlyArray<ClientService.IntegrationCommandDto> = []
): IActionSummary => {
  const items = result.items ?? [];
  const failures = items
    .filter((item) => !item.succeeded)
    .map((item): IActionFailure => {
      const command = commands.find(({ id }) => id === item.id);
      return { id: item.id ?? '', reason: item.reason, label: command && describeCommand(command) };
    });
  return { succeededCount: items.length - failures.length, failures };
};

// Splits pasted text into event ids: they are separated by white space, commas or semicolons, may come in
// braces or quotes, and are returned in lower case, distinct, in order of first appearance.
export const parseEventIds = (text: string): IReplayParse => {
  const validIds: string[] = [];
  const invalidTokens: string[] = [];
  const seen = new Set<string>();
  let duplicateCount = 0;

  text.split(ID_SEPARATORS).forEach((rawToken) => {
    const token = rawToken.replace(ID_WRAPPERS, '');
    if (!token) return;
    const id = token.toLowerCase();
    if (!GUID_PATTERN.test(id) || id === EMPTY_GUID) {
      invalidTokens.push(token);
      return;
    }
    if (seen.has(id)) {
      duplicateCount += 1;
      return;
    }
    seen.add(id);
    validIds.push(id);
  });

  return { validIds, invalidTokens, duplicateCount };
};

const shortenToken = (token: string): string =>
  token.length > MAX_TOKEN_LENGTH_SHOWN ? `${token.slice(0, MAX_TOKEN_LENGTH_SHOWN)}${TOKEN_ELLIPSIS}` : token;

export const formatInvalidTokens = (tokens: ReadonlyArray<string>): string =>
  tokens.slice(0, MAX_INVALID_TOKENS_SHOWN).map(shortenToken).join(', ');

// Only a flag known to be off blocks the replay: when the state is unknown the server answers with a clear message.
export const canReplay = (parse: IReplayParse, isIntegrationEnabled?: boolean): boolean =>
  isIntegrationEnabled !== false &&
  parse.invalidTokens.length === 0 &&
  parse.validIds.length >= 1 &&
  parse.validIds.length <= MAX_REPLAY_IDS;

const isReplayItemStatus = (status: string): status is ReplayItemStatus => hasOwn(REPLAY_ITEM_PRESENTATION, status);

export const getReplayItemPresentation = (status?: string): IReplayItemPresentation | undefined =>
  status && isReplayItemStatus(status) ? REPLAY_ITEM_PRESENTATION[status] : undefined;

export const summariseReplayResult = (result: ClientService.ReplayEventsResultDto): IReplaySummary => {
  const items = result.items ?? [];
  const countOf = (status: ReplayItemStatus) => items.filter((item) => item.status === status).length;
  return {
    requested: result.requestedCount ?? items.length,
    accepted: result.acceptedCount ?? countOf('Accepted'),
    notFound: countOf('NotFound'),
    expired: countOf('Expired'),
  };
};

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

// The generated client throws the ABP error envelope itself for the statuses it declares. For any other status
// (502, 503...) it throws an ApiException that keeps the body as text in `response` and has no `result`.
export const getErrorMessage = (error: unknown, fallback: string): string => {
  const envelope: unknown =
    isObject(error) && ClientService.ApiException.isApiException(error)
      ? error.result ?? parseJson(error.response)
      : error;
  const message = isObject(envelope) ? (envelope as { error?: { message?: unknown } }).error?.message : undefined;
  return typeof message === 'string' && message ? message : fallback;
};
